// Filter bar — slim row between the toolbar and the matrix grid. The
// input updates `local.query` and re-applies the DOM-class filter
// in-place on every keystroke (without going through notify()), so
// typing never re-renders the matrix and the input never loses focus.
//
// Visibility is controlled by a `body--filter-open` class on `<body>`;
// the bar itself stays in the DOM so the matrix layout and the input's
// preserved value don't churn on every show/hide.

import { local } from "../state";
import { matches, parseQuery, writeHashQuery } from "../search";

const BODY_OPEN = "body--filter-open";

/** Show the bar and focus its input. Idempotent. */
export function showFilterBar(): void {
  document.body.classList.add(BODY_OPEN);
  const input = document.querySelector<HTMLInputElement>(".filter-bar__input");
  input?.focus();
  input?.select();
}

/** Hide the bar without clearing its contents. The active filter (if
 *  any) keeps applying — re-opening reveals the same query. */
export function hideFilterBar(): void {
  document.body.classList.remove(BODY_OPEN);
  const input = document.querySelector<HTMLInputElement>(".filter-bar__input");
  input?.blur();
}

export function toggleFilterBar(): void {
  if (isFilterBarVisible()) hideFilterBar();
  else showFilterBar();
}

export function isFilterBarVisible(): boolean {
  return document.body.classList.contains(BODY_OPEN);
}

export function renderFilterBar(): HTMLElement {
  const bar = document.createElement("div");
  bar.className = "filter-bar";

  const wrap = document.createElement("label");
  wrap.className = "filter-bar__wrap";

  const icon = svgIcon(ICON_SEARCH);
  icon.classList.add("filter-bar__icon");

  const input = document.createElement("input");
  input.type = "search";
  input.className = "filter-bar__input";
  input.placeholder = "Filter tasks — e.g. tag:travel hotel";
  input.setAttribute("aria-label", "Filter tasks");
  input.value = local.query;

  const clear = document.createElement("button");
  clear.type = "button";
  clear.className = "filter-bar__clear";
  clear.hidden = local.query === "";
  clear.textContent = "×";
  clear.setAttribute("aria-label", "Clear filter");

  const onInput = () => {
    // Keystrokes only mutate `local.query` and update DOM classes —
    // they intentionally skip notify() to avoid stealing focus by
    // triggering a full re-render.
    local.query = input.value;
    clear.hidden = input.value === "";
    applyFilterToDom();
    writeHashQuery(input.value);
  };
  input.addEventListener("input", onInput);

  clear.addEventListener("click", () => {
    input.value = "";
    onInput();
    input.focus();
  });

  // Escape: clear contents on first press, hide the bar on the next
  // (or hide immediately if the input was already empty).
  input.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (input.value) {
        input.value = "";
        onInput();
      } else {
        hideFilterBar();
      }
    }
  });

  wrap.append(icon, input, clear);
  bar.append(wrap);
  return bar;
}

/** Toggle `.task--filtered-out` on every visible task row to match the
 *  current `local.query`. Called from the input handler and from any
 *  external code that mutates the query (e.g. clicking a hashtag in a
 *  row, or `setQuery` from a hashchange event). */
export function applyFilterToDom(): void {
  const q = parseQuery(local.query);
  for (const li of document.querySelectorAll<HTMLLIElement>("li.task")) {
    const id = li.dataset.taskId;
    if (!id) continue;
    const task = local.state.tasks[id];
    if (!task) continue;
    li.classList.toggle("task--filtered-out", !matches(task.label, q));
  }
}

const ICON_SEARCH =
  "M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z";

function svgIcon(pathD: string): SVGSVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "16");
  svg.setAttribute("height", "16");
  svg.setAttribute("fill", "currentColor");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  const path = document.createElementNS(ns, "path");
  path.setAttribute("d", pathD);
  svg.appendChild(path);
  return svg;
}
